import { useLayoutEffect, useMemo, useRef, useState } from "react";

import { FloatingPortal } from "@floating-ui/react";
import { faMinus, faPlus, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { faArrowTurnDownRight } from "@fortawesome/pro-solid-svg-icons";
import { times } from "lodash";

import {
  computeToolCallStatus,
  ToolCallAccordionItem,
} from "src/agents/base/ToolCallDisplay";
import { Chat } from "src/base-components/Chat/Chat";
import { ChatEmptyStatePrompt } from "src/base-components/Chat/types";
import {
  FloatingWindowInternal,
  useFloatingWindowPosition,
} from "src/base-components/FloatingWindow/FloatingWindow";
import { useFloatingWindowsActions } from "src/base-components/FloatingWindow/hooks";
import { FloatingWindowProps } from "src/base-components/FloatingWindow/store";
import { Pill } from "src/base-components/Pill";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { parseMessageReference } from "src/cases/copilot/parseMessageReference";
import type { CopilotChatMessage } from "src/cases/copilot/useCaseCopilotSession";
import { useCaseCopilotSession } from "src/cases/copilot/useCaseCopilotSession";
import { useCase } from "src/cases/queries/cases";
import { FeatureQueryConversationContextContextTypeEnum } from "src/clients/copilot";
import { CopilotTitle } from "src/copilot/CopilotTitle";
import { PromptBox } from "src/copilot/PromptBox";
import { TitleHelpContent } from "src/copilot/TitleHelpContent";
import { Callout } from "src/design-system/Callout";
import { Icon, IconButton } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { CasePageParams } from "src/router/urls";
import { useCurrentUserId } from "src/store/AuthStore";
import { useParamsDecode } from "src/utils/useParamsDecode";

const SUGGESTION_PROMPTS: ChatEmptyStatePrompt[] = [
  {
    label: "What is the source of the funds in question?",
    prompt: "What is the source of the funds in question?",
  },
  {
    label: "Any unusual transactions recently?",
    prompt: "Have there been any unusual transactions recently?",
  },
  {
    label: "Any connections to known criminal entitites?",
    prompt: "Are there any connections to known criminal entitites?",
  },
];

const MessageSkeleton: React.FC = () => (
  <li className="flex flex-col gap-y-2">
    <SkeletonPlaceholder height="h-3" width="w-14" />
    <SkeletonPlaceholder height="h-4" width="w-full" />
    <SkeletonPlaceholder height="h-4" width="w-full" />
    <SkeletonPlaceholder height="h-4" width="w-3/4" />
  </li>
);

const ReferenceChip: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex w-fit items-center gap-x-0.5">
    <Icon icon={faArrowTurnDownRight} size="2xs" />
    <Pill size="sm" variant="gray">
      <Pill.Text>{label}</Pill.Text>
    </Pill>
  </div>
);

const CopilotMessage: React.FC<{
  message: CopilotChatMessage;
}> = ({ message }) => {
  if (message.role === "tool-group") {
    return (
      <li className="space-y-2">
        {message.toolCallsWithResults.map((tcwr) => (
          <ToolCallAccordionItem
            key={tcwr.toolCall.id}
            status={computeToolCallStatus(tcwr.toolResult, null)}
            toolCall={tcwr.toolCall}
            toolResult={tcwr.toolResult}
          />
        ))}
      </li>
    );
  }

  if (message.role === "user") {
    const { ref, text } = parseMessageReference(message.content);
    return (
      <Chat.Message role="user">
        {ref && (
          <div className="mb-1">
            <ReferenceChip label={ref} />
          </div>
        )}
        <Chat.TextContent animate={false} content={text} delay={0} />
      </Chat.Message>
    );
  }

  return (
    <Chat.Message role={message.role}>
      <Chat.TextContent animate={false} content={message.content} delay={0} />
    </Chat.Message>
  );
};

const TitleRight: React.FC<{ onReset: VoidFunction }> = ({ onReset }) => (
  <>
    <span className="ml-auto text-gray-200">|</span>
    <Tooltip placement="bottom" title="Start a new chat" asChild>
      <IconButton icon={faPlus} size="xs" onClick={onReset} />
    </Tooltip>
  </>
);

export const CaseCopilotWindow: React.FC<FloatingWindowProps> = ({
  computePosition,
  id,
  data: windowData,
}) => {
  const { position, windowRef } = useFloatingWindowPosition(computePosition);
  const { close: closeWindow, updateData } = useFloatingWindowsActions();
  const { case_id: caseId } = useParamsDecode<CasePageParams>();
  const userId = useCurrentUserId();
  const { data: caseData } = useCase(caseId);

  const copilotReference = caseData?.data.template_version?.copilot_reference;
  const agentId = copilotReference?.agent_id;
  const agentVersionId = copilotReference?.latest_agent_version_id;
  const environment = caseData?.data.env ?? "sandbox";

  const refLabel = windowData?.refLabel as string | undefined;
  const clearRefLabel = () => {
    updateData(id, {});
  };

  const { messages, isLoading, isActive, failureMessage, sendMessage } =
    useCaseCopilotSession({
      agentId,
      agentVersionId,
      caseId,
      userId,
      environment,
    });

  const chronologicalMessages = useMemo(
    () => [...messages].reverse(),
    [messages],
  );

  const [prompt, setPrompt] = useState("");
  const listRef = useRef<HTMLUListElement>(null);
  const anchorMessageRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list || !anchorMessageRef.current) return;

    const messageIndex = chronologicalMessages.findIndex(
      (m) => m.role === "user" && m.content === anchorMessageRef.current,
    );

    if (messageIndex >= 0) {
      requestAnimationFrame(() => {
        const messageElements = list.children;
        if (messageElements[messageIndex]) {
          const element = messageElements[messageIndex] as HTMLElement;
          list.scrollTop = element.offsetTop - 20;
        }
      });
    }
  }, [chronologicalMessages]);

  const handleSend = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;

    const message = refLabel
      ? `<ref label="${refLabel}"/>\n${trimmed}`
      : trimmed;
    anchorMessageRef.current = message;
    sendMessage(message);
    setPrompt("");
    clearRefLabel();
  };

  const handleReset = () => {
    setPrompt("");
    anchorMessageRef.current = null;
    clearRefLabel();
  };

  return (
    <FloatingPortal>
      <FloatingWindowInternal
        ref={windowRef}
        clipOverflow={false}
        closeIcon={faMinus}
        height="h-150"
        helpText={
          <TitleHelpContent description="Ask questions about this case and get help with analysis, actions, and next steps." />
        }
        isPinned={false}
        maximizable={false}
        style={{
          inset: "0px auto auto 0px",
          transform: position,
        }}
        title={<CopilotTitle />}
        titleRight={<TitleRight onReset={handleReset} />}
        draggable
        onClose={() => closeWindow(id)}
      >
        <Chat.Body>
          {messages.length > 0 && (
            <Chat.MessageList ref={listRef}>
              {chronologicalMessages.map((message, index) => (
                <CopilotMessage key={`msg-${index}`} message={message} />
              ))}
              {failureMessage && (
                <li className="mt-4 flex-shrink-0">
                  <Callout type="error">{failureMessage}</Callout>
                </li>
              )}
              {isActive && <Chat.LoadingIndicator mode="loading" />}
            </Chat.MessageList>
          )}
          {messages.length === 0 && isLoading && (
            <Chat.MessageList>
              {times(3, (i) => (
                <MessageSkeleton key={i} />
              ))}
            </Chat.MessageList>
          )}
          {messages.length === 0 && !isLoading && (
            <Chat.EmptyState
              prompts={SUGGESTION_PROMPTS}
              onPromptClick={handleSend}
            />
          )}
          <Chat.InputArea>
            <PromptBox
              disabled={isActive || prompt.trim().length === 0}
              errored={false}
              mentionTextPlaceholder=""
              minified={messages.length > 0}
              resource={[]}
              resourceType={
                FeatureQueryConversationContextContextTypeEnum.FEATURE_QUERY
              }
              topContent={
                refLabel && (
                  <div className="flex items-center justify-between">
                    <ReferenceChip label={refLabel} />
                    <IconButton
                      icon={faXmark}
                      size="2xs"
                      onClick={clearRefLabel}
                    />
                  </div>
                )
              }
              value={prompt}
              onChange={setPrompt}
              onReset={handleReset}
              onSubmit={handleSend}
            />
          </Chat.InputArea>
        </Chat.Body>
      </FloatingWindowInternal>
    </FloatingPortal>
  );
};
